import { useState, useCallback, useRef } from 'react';
import { validarArchivos, tipoDeArchivo, MAX_ARCHIVOS } from './adjuntos';
import { comprimirImagen } from './comprimir';

// Archivos elegidos para el mensaje que se está escribiendo, con su mensaje de error.
// Las fotos se comprimen antes de aceptarlas (tardan un instante): mientras tanto `procesando` es true y la pantalla no debe
// enviar. `limpiar` y `quitar` son estables, así que se pueden usar como dependencia de un efecto.
export const useAdjuntos = () => {
  const [archivos, setArchivos] = useState([]);
  const [error, setError] = useState('');
  const [procesando, setProcesando] = useState(false);
  const ultimos = useRef([]);        // la lista más reciente: después de esperar no se puede usar la que había al empezar
  const generacion = useRef(0);      // cambia al limpiar: lo que se estaba procesando de antes ya no se agrega
  const enCurso = useRef(0);

  const fijar = useCallback((lista) => {
    ultimos.current = lista;
    setArchivos(lista);
  }, []);

  const agregar = async (nuevos) => {
    const elegidos = Array.from(nuevos);
    const mia = generacion.current;
    enCurso.current += 1;
    setProcesando(true);
    try {
      // Solo se optimizan las fotos que caben en el mensaje; el resto se rechaza abajo por la cantidad
      const lugar = Math.max(0, MAX_ARCHIVOS - ultimos.current.length);
      const listos = await Promise.all(elegidos.map((f, i) => (i < lugar && tipoDeArchivo(f) === 'imagen' ? comprimirImagen(f) : f)));
      if (mia !== generacion.current) return;   // mientras tanto se cambió de conversación o se envió: esto ya no va
      const { aceptados, error: motivo } = validarArchivos(ultimos.current, listos);
      if (aceptados.length) fijar([...ultimos.current, ...aceptados]);
      setError(motivo);
    } finally {
      if (mia === generacion.current) {
        enCurso.current -= 1;
        if (enCurso.current <= 0) setProcesando(false);
      }
    }
  };

  const quitar = useCallback((indice) => {
    fijar(ultimos.current.filter((_, i) => i !== indice));
    setError('');
  }, [fijar]);

  const limpiar = useCallback(() => {
    generacion.current += 1;
    enCurso.current = 0;
    setProcesando(false);
    fijar([]);
    setError('');
  }, [fijar]);

  return { archivos, error, setError, procesando, agregar, quitar, limpiar };
};
